import {
    ArrowLeftIcon,
    EyeIcon,
    FileIcon,
    FolderIcon,
    FolderUpIcon,
    PlusSquareIcon,
    Trash2Icon,
    UploadIcon
} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Separator} from "@/components/ui/separator.tsx";
import {ChangeEventHandler, useEffect, useRef, useState} from "react";
import {
    Dialog, DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"


import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import axios, {AxiosError} from "axios";
import {toast} from "sonner";
import Restricted from "@/components/permissions/Restricted.tsx";
import {DownloadIcon} from "@radix-ui/react-icons";
import {useAuth} from "react-oidc-context";
import {Spinner} from "@/components/minimal-tiptap/components/spinner.tsx";

export default function FilesPage() {

    const auth = useAuth()
    const [path, setPath] = useState<string>('')
    const [inputPath, setInputPath] = useState<string>('')
    const [items, setItems] = useState<any>({})
    const [loading, setLoading] = useState<boolean>(false)
    useEffect(() => {
        loadFiles(path)
    }, [path]);

    function loadFiles(p: string) {
        setLoading(true)
        axios.get("/api/files", {
            params: {
                path: p
            }
        }).then((res) => {
            if (res.status == 200) {
                setItems(res.data)
            }
        }).catch((err) => {
            console.error(err)
        }).finally(() => {
            setLoading(false)
        })
    }

    const fileUploadRef = useRef<HTMLInputElement>(null)

    const handleFileUploadChange: ChangeEventHandler = (event) => {
        const files = fileUploadRef.current?.files

        if (!files || files.length < 1) return;
        event.preventDefault()

        axios.post("/api/files", {
            path: path,
            name: files[0].name,
        }).then((res) => {
            const token = res.data.upload_token

            const formData = new FormData();
            formData.append('file', files[0]);
            formData.append('upload_token', token);
            axios.post(import.meta.env.VITE_ATTACHMENT_UPLOAD_URL, formData, {withXSRFToken: false}).then(res => {
                if (res.status !== 200) {
                    console.log(res.data)
                    toast.error('Hiba történt a feltöltés során!');

                    return;
                }
                toast.success('Sikeresen feltöltve!');
            }).catch((err: AxiosError) => {
                console.error(err)
                if (err.status === 413) {
                    toast.error('A fájl túl nagy!');
                    return;
                }
                toast.error('Hiba történt a feltöltés során!');
            }).finally(() => {
                loadFiles(path)
            })

        }).catch((err) => {
            console.error(err)
        }).finally(() => {
            //@ts-ignore
            fileUploadRef.current.value = null;
        })
    }


    const getShortFilename = (file: string) => {
        const parts = file.split('/')

        const filename = parts.at(parts.length - 1)

        if (filename) {
            const filename_parts = filename.split('.')

            const name = filename_parts.at(0)
            const extension = filename_parts.at(1)

            if (name && extension) {
                if (name.length > 15) {
                    return name.substring(0, 15) + '...' + extension
                } else {
                    return filename
                }
            }
            return filename
        }
    }


    const deleteFile = (file: string) => {
        axios.delete("/api/files", {
            params: {
                file: file
            }
        }).then((res) => {
            console.log(res)
            loadFiles(path)
        }).catch((err) => {
            console.error(err)
        })
    }

    const viewFile = (file: string) => {
        axios.get("/api/files/link", {
            params: {
                file: file,
            }
        }).then((res) => {
            const a = document.createElement('a')
            a.setAttribute('href', res.data.url)
            a.setAttribute('target', '_blank')
            document.body.appendChild(a)
            a.click()
            a.remove()
        })
    }

    const downloadFile = (file: string) => {
        const a = document.createElement('a')
        a.setAttribute('href', `/api/files/download?file=${file}&api_key=${auth.user?.access_token}`)
        a.setAttribute('target', '_blank')
        a.setAttribute('download', '')
        document.body.appendChild(a)
        a.click()
        a.remove()
    }


    return <div className={"px-4 w-full h-full"}>

        <div className={"flex justify-between"}>
            <h1 className={"text-sidebar font-black text-2xl mb-4"}>Dokumentumtár</h1>

            <Restricted to={"user.any"}>
                <Button className={"inline-flex gap-2"} onClick={() => fileUploadRef.current?.click()}><UploadIcon/>Feltöltés</Button>
            </Restricted>
            <input type={"file"} hidden ref={fileUploadRef} onChange={handleFileUploadChange}
                   accept=".pdf,.png,.jpeg,.jpg,.docx,.doc,.xlsx,.xls"/>
        </div>

        <Separator></Separator>
        <div className={"flex justify-between gap-2  py-1"}>

            <div className={"flex items-center gap-2"}>
                <Button variant={'ghost'} onClick={() => setPath(path.split('/').slice(0, -1).join('/'))}>
                    <ArrowLeftIcon/>
                </Button>

                {
                    path.split('/').map((p, i) => (
                        <>{
                            i < path.split('/').length - 1 ? <>
                                    <a className={"hover:text-primary"} key={p} href={"javascript:void(0)"}
                                       onClick={() => setPath(p)}>{p}</a>
                                    <span>/</span>
                                </> :
                                <span>{p}</span>
                        }
                        </>
                    ))
                }
            </div>

            <Restricted to={"user.any"}>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button variant={'outline'} className={"inline-flex gap-2"}
                                onClick={() => setInputPath(path)}><PlusSquareIcon/>Új mappa
                            útvonal</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>Új mappa útvonal</DialogTitle>
                            <DialogDescription>
                                Adja meg a létrehozni kívánt mappa útvonalát. Az útvonal az első fájl feltöltésekor
                                véglegesül.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4">
                            <div className="grid grid-rows-2 items-center">
                                <Label htmlFor="path">
                                    Útvonal
                                </Label>
                                <Input id="path" placeholder="uj/mappa" className="col-span-3" value={inputPath}
                                       onChange={(e) => setInputPath(e.target.value)}/>
                            </div>
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="submit" onClick={() => {
                                    setPath(inputPath)
                                    setInputPath('')
                                }}>Létrehozás</Button>
                            </DialogClose>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Restricted>
        </div>

        <Separator></Separator>

        { loading ? <div className={"flex justify-center mt-4 p-2"}><Spinner className={"size-8"}/></div> :
        <div className={"grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 mt-4 gap-3"}>

            <div
                className={"text-muted-foreground text-sm hover:text-primary cursor-pointer flex flex-col items-center justify-start gap-2"}
                onClick={() => setPath(path.split('/').slice(0, -1).join('/'))}
            >
                <FolderUpIcon className={"size-7"}/>
                <p className={"text-center"}>Vissza</p>
            </div>

            {
                items.directories?.map((item: string) => (
                    <div
                        className={"text-muted-foreground text-sm hover:text-primary cursor-pointer flex flex-col items-center justify-start gap-2"}
                        key={item} onClick={() => setPath(item)}>
                        <FolderIcon className={"size-7"}/>
                        <p className={"text-center"}>{item.split('/').splice(-1)}</p>
                    </div>
                ))
            }

            {
                items.files?.map((item: string) => (

                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <div
                                className={"text-muted-foreground text-sm hover:text-primary cursor-pointer flex flex-col items-center justify-start gap-2"}
                                key={item}>
                                <FileIcon className={"size-7"}/>
                                <p className={"text-center"}
                                   title={item.split('/').splice(-1).at(0)}>{getShortFilename(item)}</p>
                            </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuLabel>Fájl műveletek</DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem onClick={() => viewFile(item)}><EyeIcon/>Megtekintés</DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => downloadFile(item)}><DownloadIcon/>Letöltés</DropdownMenuItem>
                            <Restricted to={"user.any"}>
                                <DropdownMenuItem onClick={() => {
                                    deleteFile(item)
                                }}
                                                  className={"text-destructive"}
                                ><Trash2Icon className={"text-destructive"}/>Törlés</DropdownMenuItem>
                            </Restricted>
                        </DropdownMenuContent>
                    </DropdownMenu>
                ))
            }
        </div>}

    </div>
}